import { Builder } from '@builder.io/sdk';
import { PageHero } from './PageHero';
import {
  FIXED_DURATION_ONE_DAY,
  FIXED_DURATION_ONE_WEEK,
  FIXED_DURATION_TWO_DAYS,
} from '../LandingPageForm/LandingPageForm';
import { BuilderInputOptions } from '@/types/builder';
import { CONTENT_TYPES } from './PageHero.types';
import { journeyInput, showDurationPickerInput } from '../LandingPageForm/LandingPageForm.registry';

const ProductPageHeaderConfig = {
  name: 'PageHero',
  inputs: [
    {
      name: 'headingOnly',
      type: 'boolean',
      required: false,
      defaultValue: false,
      description: 'Display only the heading without any other content',
    },
    {
      name: 'title',
      type: 'string',
      required: true,
    },
    {
      name: 'subtitle',
      type: 'string',
      required: false,
      showIf: (options: BuilderInputOptions) => !options?.get('headingOnly'),
    },
    {
      name: 'tagline',
      type: 'richText',
      required: false,
      showIf: (options: BuilderInputOptions) => !options?.get('headingOnly'),
    },
    {
      name: 'enableForm',
      type: 'boolean',
      required: false,
      defaultValue: false,
      description: 'Enable form. This will hide the image.',
      showIf: (options: BuilderInputOptions) => !options?.get('headingOnly'),
    },
    { ...journeyInput, showIf: (options: BuilderInputOptions) => !!options?.get('enableForm') },
    {
      name: 'regLabel',
      type: 'string',
      required: false,
      defaultValue: 'Enter Registration Number',
      description: 'Label for the registration number field',
      showIf: (options: BuilderInputOptions) => !!options?.get('enableForm'),
    },
    {
      name: 'defaultDuration',
      type: 'string',
      required: false,
      defaultValue: FIXED_DURATION_TWO_DAYS,
      enum: [
        { label: 'One day', value: FIXED_DURATION_ONE_DAY },
        { label: 'Two days', value: FIXED_DURATION_TWO_DAYS },
        { label: 'One week', value: FIXED_DURATION_ONE_WEEK },
      ],
      description: 'Default duration for the form',
      showIf: (options: BuilderInputOptions) => !!options?.get('enableForm'),
    },
    {
      ...showDurationPickerInput,
      showIf: (options: BuilderInputOptions) => !!options?.get('enableForm'),
    },
    {
      name: 'contentType',
      type: 'string',
      enum: Object.values(CONTENT_TYPES),
      defaultValue: CONTENT_TYPES.LIST,
      required: true,
      description: 'Content type to display',
      showIf: (options: BuilderInputOptions) => !options?.get('headingOnly'),
    },
    {
      name: 'points',
      type: 'list',
      required: false,
      showIf: (options: BuilderInputOptions) => options?.get('contentType') === 'List',
      description: 'List of points with tick icons to display',
      subFields: [
        {
          name: 'point',
          type: 'string',
        },
      ],
    },
    {
      name: 'body',
      type: 'richText',
      required: false,
      showIf: (options: BuilderInputOptions) =>
        options?.get('contentType') === 'Text' || options?.get('contentType') === 'Trustpilot',
      description: 'Text to display',
    },
    {
      name: 'image',
      type: 'file',
      required: false,
      showIf: (options: BuilderInputOptions) => !options?.get('enableForm'),
    },
    {
      name: 'imageFit',
      type: 'enum',
      enum: [
        { label: 'contain', value: 'contain' },
        { label: 'cover', value: 'cover' },
      ],
      required: false,
      defaultValue: 'cover',
      showIf: (options: BuilderInputOptions) => !options?.get('enableForm'),
    },
    {
      name: 'imagePosition',
      type: 'enum',
      enum: [
        { label: 'left', value: 'left' },
        { label: 'right', value: 'right' },
      ],
      required: false,
      defaultValue: 'right',
      showIf: (options: BuilderInputOptions) => !options?.get('enableForm'),
    },
    {
      name: 'showAppLinks',
      type: 'boolean',
      required: false,
      defaultValue: false,
      description: 'Show app store links',
      showIf: (options: BuilderInputOptions) => !options?.get('headingOnly'),
    },
    {
      name: 'appLinks',
      type: 'reference',
      model: 'store-links',
    },
  ],
};

Builder.registerComponent(PageHero, ProductPageHeaderConfig);
